import RequireOwner from "@/components/RequireOwner";
import { useOwnerAuth } from "@/components/owner/OwnerAuthProvider";
import StepHeader from "@/components/owner/StepHeader";
import { getWebRecaptcha, linkPhoneNumber } from "@/lib/authPhone";
import { router } from "expo-router";
import React, { useMemo, useState } from "react";
import { Platform, Pressable, Text, TextInput, View } from "react-native";

export default function PhoneBackup() {
  const { user } = useOwnerAuth();
  const [phone, setPhone] = useState("+254");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"enter" | "verify">("enter");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [confirmFn, setConfirmFn] =
    useState<((code: string) => Promise<boolean>) | null>(null);

  const webVerifier = useMemo(() => {
    if (Platform.OS !== "web") return null;
    return getWebRecaptcha("recaptcha-container");
  }, []);

  const isLoggedIn = !!user;

  const sendOtp = async () => {
    setError("");
    setBusy(true);
    try {
      const flow = await linkPhoneNumber({
        phone: phone.trim(),
        webVerifier,
      });
      setConfirmFn(() => flow.confirm);
      setStep("verify");
    } catch (e: any) {
      setError(e?.message || "Could not send OTP");
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    if (!confirmFn) return;
    setError("");
    setBusy(true);
    try {
      await confirmFn(code.trim());
      router.back();
    } catch (e: any) {
      setError(e?.message || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <RequireOwner>
      {!isLoggedIn ? (
        <View style={{ flex: 1, padding: 16, justifyContent: "center" }}>
          <Text style={{ fontWeight: "900" }}>
            Please sign in with Google first.
          </Text>
          <Pressable onPress={() => router.replace("/(owner)/onboarding/signin")}>
            <Text style={{ fontWeight: "900", color: "#2563eb", marginTop: 10 }}>
              Go to login
            </Text>
          </Pressable>
        </View>
      ) : (
        <View style={{ flex: 1 }}>
          <StepHeader
            title="Backup Phone OTP"
            subtitle="Optional (recommended) — links phone to your Google login."
          />

          {Platform.OS === "web" ? (
            <View
              // @ts-ignore
              id="recaptcha-container"
              style={{ height: 1, width: 1, opacity: 0 }}
            />
          ) : null}

          <View style={{ padding: 16, gap: 10 }}>
            {step === "enter" ? (
              <>
                <Text style={{ fontWeight: "900" }}>Phone number</Text>
                <TextInput
                  value={phone}
                  onChangeText={setPhone}
                  style={input}
                  keyboardType="phone-pad"
                />

                {!!error && (
                  <Text style={{ color: "#b91c1c", fontWeight: "800" }}>
                    {error}
                  </Text>
                )}

                <Pressable
                  onPress={sendOtp}
                  style={[btnDark, busy ? { opacity: 0.6 } : null]}
                  disabled={busy}
                >
                  <Text style={{ color: "#fff", fontWeight: "900" }}>
                    {busy ? "Sending..." : "Send OTP"}
                  </Text>
                </Pressable>

                <Pressable onPress={() => router.back()} style={btnLight}>
                  <Text style={{ fontWeight: "900" }}>Skip</Text>
                </Pressable>
              </>
            ) : (
              <>
                <Text style={{ fontWeight: "900" }}>Enter OTP code</Text>
                <TextInput
                  value={code}
                  onChangeText={setCode}
                  style={input}
                  keyboardType="number-pad"
                />

                {!!error && (
                  <Text style={{ color: "#b91c1c", fontWeight: "800" }}>
                    {error}
                  </Text>
                )}

                <Pressable
                  onPress={verifyOtp}
                  style={[btnDark, busy ? { opacity: 0.6 } : null]}
                  disabled={busy}
                >
                  <Text style={{ color: "#fff", fontWeight: "900" }}>
                    {busy ? "Verifying..." : "Verify & Link"}
                  </Text>
                </Pressable>
              </>
            )}
          </View>
        </View>
      )}
    </RequireOwner>
  );
}

const input = {
  height: 46,
  borderRadius: 14,
  borderWidth: 1,
  borderColor: "#e5e7eb",
  paddingHorizontal: 12,
  backgroundColor: "#fff",
  fontWeight: "800" as const,
};

const btnDark = {
  height: 48,
  borderRadius: 14,
  backgroundColor: "#111827",
  alignItems: "center" as const,
  justifyContent: "center" as const,
};

const btnLight = {
  height: 46,
  borderRadius: 14,
  borderWidth: 1,
  borderColor: "#e5e7eb",
  backgroundColor: "#fff",
  alignItems: "center" as const,
  justifyContent: "center" as const,
};
